import { useState, useEffect } from "react";
import { supabase } from "../../../supabaseClient";
import { getProductPhotosPrefix } from "../../../components/eventStorageConfig";
import "./Step0.css";

const MAX_PHOTOS = 6;
const MIN_PHOTOS = 3;

const sanitize = (name) =>
  name?.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_-]/g, "");

export default function Step0Branding({ vendor, event, app, setCanProceed }) {
  const eventKey = event?.event_key || event?.id;

  // Logo
  const [logoFile, setLogoFile]       = useState(null);
  const [logoPreview, setLogoPreview] = useState(vendor?.logo_url || null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoSaved, setLogoSaved]     = useState(
    vendor?.logo_path && vendor?.logo_url ? { path: vendor.logo_path, url: vendor.logo_url } : null
  );

  // Product photos
  const [photos, setPhotos]           = useState([]); // [{ path, url, name }]
  const [photoUploading, setPhotoUploading] = useState(false);

  const [loading, setLoading]         = useState(true);
  const [message, setMessage]         = useState(null); // { text, type }

  /* =========================================================
     HYDRATE EXISTING PHOTOS ON MOUNT
     (vendor + app are already loaded upstream — nothing to fetch here
     except signed URLs for whatever photo paths are already on the app)
  ========================================================= */
  useEffect(() => {
    const hydrate = async () => {
      try {
        if (app?.product_photo_paths?.length > 0) {
          const hydrated = await Promise.all(
            app.product_photo_paths.map(async (item) => {
              const { data } = await supabase.storage
                .from("product_photos")
                .createSignedUrl(item.path, 60 * 60);
              return { ...item, url: data?.signedUrl || "" };
            })
          );
          setPhotos(hydrated);
        }
      } catch (err) {
        console.error("Step0 photo hydrate error:", err);
      } finally {
        setLoading(false);
      }
    };

    hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.id]);

  /* =========================================================
     CAN PROCEED — need logo + at least 3 photos
  ========================================================= */
  useEffect(() => {
    const hasLogo   = !!logoSaved;
    const hasPhotos = photos.length >= MIN_PHOTOS;
    setCanProceed(hasLogo && hasPhotos);
  }, [logoSaved, photos, setCanProceed]);

  /* =========================================================
     LOGO — SELECT PREVIEW
  ========================================================= */
  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  /* =========================================================
     LOGO — UPLOAD (not event-scoped — a vendor's logo is shared across
     every event they apply to, so no eventKey involved here)
  ========================================================= */
  const handleLogoUpload = async () => {
    if (!logoFile || !vendor) return;
    setLogoUploading(true);
    setMessage(null);

    try {
      const safeBrand = sanitize(vendor.brand_name);
      const ext       = logoFile.name.split(".").pop();
      const path      = `${safeBrand}/logo.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from("logos")
        .upload(path, logoFile, { upsert: true });
      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage.from("logos").getPublicUrl(path);
      const publicUrl = urlData.publicUrl;

      const { error: updateErr } = await supabase
        .from("vendors")
        .update({ logo_path: path, logo_url: publicUrl })
        .eq("id", vendor.id);
      if (updateErr) throw updateErr;

      setLogoSaved({ path, url: publicUrl });
      setLogoFile(null);
      setMessage({ text: "Logo saved!", type: "success" });
    } catch (err) {
      setMessage({ text: "Logo upload failed: " + err.message, type: "error" });
    } finally {
      setLogoUploading(false);
    }
  };

  /* =========================================================
     LOGO — REMOVE
  ========================================================= */
  const handleLogoRemove = async () => {
    if (!logoSaved || !vendor) return;

    try {
      await supabase.storage.from("logos").remove([logoSaved.path]);
      await supabase
        .from("vendors")
        .update({ logo_path: null, logo_url: null })
        .eq("id", vendor.id);

      setLogoSaved(null);
      setLogoPreview(null);
      setLogoFile(null);
      setMessage({ text: "Logo removed.", type: "info" });
    } catch (err) {
      setMessage({ text: "Failed to remove logo.", type: "error" });
    }
  };

  /* =========================================================
     PHOTOS — UPLOAD (multiple) — event-scoped via folder prefix
  ========================================================= */
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !vendor || !app?.id) return;

    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setMessage({ text: `Maximum ${MAX_PHOTOS} photos allowed.`, type: "error" });
      return;
    }

    const toUpload = files.slice(0, remaining);
    setPhotoUploading(true);
    setMessage(null);

    try {
      const safeBrand = sanitize(vendor.brand_name);
      const prefix    = getProductPhotosPrefix(eventKey);
      const uploaded  = [];

      for (const file of toUpload) {
        const safeFile = file.name
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-zA-Z0-9._-]/g, "_");

        const path = `${prefix}/${safeBrand}/${Date.now()}_${safeFile}`;

        const { error: uploadErr } = await supabase.storage
          .from("product_photos")
          .upload(path, file, { upsert: true });
        if (uploadErr) throw uploadErr;

        const { data: signedData } = await supabase.storage
          .from("product_photos")
          .createSignedUrl(path, 60 * 60);

        uploaded.push({ path, name: file.name, url: signedData?.signedUrl || "" });
      }

      const updatedPhotos = [...photos, ...uploaded];
      setPhotos(updatedPhotos);

      const { error: saveErr } = await supabase
        .from("vendor_applications")
        .update({
          product_photo_paths: updatedPhotos.map((p) => ({ path: p.path, name: p.name })),
        })
        .eq("id", app.id);
      if (saveErr) throw saveErr;

      setMessage({
        text: `${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} uploaded!`,
        type: "success",
      });
    } catch (err) {
      setMessage({ text: "Photo upload failed: " + err.message, type: "error" });
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  /* =========================================================
     PHOTOS — REMOVE ONE
  ========================================================= */
  const handlePhotoRemove = async (index) => {
    const photo = photos[index];
    if (!photo || !app?.id) return;

    try {
      await supabase.storage.from("product_photos").remove([photo.path]);

      const updatedPhotos = photos.filter((_, i) => i !== index);
      setPhotos(updatedPhotos);

      await supabase
        .from("vendor_applications")
        .update({
          product_photo_paths: updatedPhotos.map((p) => ({ path: p.path, name: p.name })),
        })
        .eq("id", app.id);

      setMessage({ text: "Photo removed.", type: "info" });
    } catch (err) {
      setMessage({ text: "Failed to remove photo.", type: "error" });
    }
  };

  /* =========================================================
     RENDER
  ========================================================= */
  if (loading) return (
    <div className="onboarding-step s0-branding">
      <h3>Branding & Photos</h3>
      <p className="s0-loading">Checking for existing uploads…</p>
    </div>
  );

  return (
    <div className="onboarding-step s0-branding">
      <h3>Branding & Photos 🎨</h3>
      <p>
        Upload your logo and at least <strong>{MIN_PHOTOS} product photos</strong>. These will be used for vendor spotlights and other promotional materials across our online platforms.
      </p>

      {message && (
        <div className={`s0-message s0-message--${message.type}`}>{message.text}</div>
      )}

      {/* ── Logo ── */}
      <div className="s0-section">
        <h4 className="s0-section-title">Brand Logo</h4>
        <p className="s0-section-desc">PNG or SVG preferred. Will be used on promotional materials.</p>

        <div className="s0-logo-area">
          {logoPreview ? (
            <div className="s0-logo-preview">
              <img src={logoPreview} alt="Logo preview" />
              <div className="s0-logo-actions">
                {!logoSaved && (
                  <button
                    className="s0-btn s0-btn--primary"
                    onClick={handleLogoUpload}
                    disabled={logoUploading}
                  >
                    {logoUploading ? "Saving…" : "Save Logo"}
                  </button>
                )}
                <label className="s0-btn s0-btn--secondary">
                  {logoSaved ? "Replace" : "Choose different"}
                  <input type="file" accept="image/*" onChange={handleLogoSelect} hidden />
                </label>
                {logoSaved && (
                  <button className="s0-btn s0-btn--danger" onClick={handleLogoRemove}>
                    Remove
                  </button>
                )}
              </div>
              {logoSaved && <span className="s0-saved-badge">✓ Saved</span>}
            </div>
          ) : (
            <label className="s0-upload-zone">
              <div className="s0-upload-icon">🖼️</div>
              <span className="s0-upload-label">Click to upload logo</span>
              <span className="s0-upload-hint">PNG, JPG, SVG — max 5MB</span>
              <input type="file" accept="image/*" onChange={handleLogoSelect} hidden />
            </label>
          )}
        </div>
      </div>

      {/* ── Product Photos ── */}
      <div className="s0-section">
        <h4 className="s0-section-title">
          Product Photos
          <span className="s0-count-badge">{photos.length}/{MAX_PHOTOS}</span>
        </h4>
        <p className="s0-section-desc">
          At least {MIN_PHOTOS} photos of your products or stall. High resolution preferred.
        </p>

        <div className="s0-photos-grid">
          {photos.map((photo, i) => (
            <div key={photo.path} className="s0-photo-thumb">
              <img src={photo.url} alt={photo.name} />
              <button className="s0-photo-remove" onClick={() => handlePhotoRemove(i)} title="Remove photo">
                ✕
              </button>
            </div>
          ))}

          {photos.length < MAX_PHOTOS && (
            <label className={`s0-photo-add ${photoUploading ? "s0-photo-add--uploading" : ""}`}>
              {photoUploading ? (
                <span className="s0-spinner" />
              ) : (
                <>
                  <span className="s0-add-icon">+</span>
                  <span className="s0-add-label">Add photos</span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                disabled={photoUploading}
                hidden
              />
            </label>
          )}
        </div>

        {photos.length < MIN_PHOTOS && (
          <p className="s0-photos-remaining">
            {MIN_PHOTOS - photos.length} more photo{MIN_PHOTOS - photos.length > 1 ? "s" : ""} required to continue
          </p>
        )}
      </div>
    </div>
  );
}